import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { OptionsList } from "@babackapp/customer-ui/components/option";
import { RadioGroup } from "@babackapp/customer-ui/components/radio-group";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { AlertCircleIcon, ArrowLeft } from "lucide-react";
import { Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { useReturnModeForm } from "../hooks/use-return-mode-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { CarrierOption } from "./return-mode-carrier-option";
import { RetractationOption } from "./return-mode-retractation-option";
import { StoreOption } from "./return-mode-store-option";

export function ReturnMode() {
  const {
    form,
    onSubmit,
    availableReturnModes,
    selectableStores,
    storeReturnState,
    isSubmitBlocked,
    isPickup,
    pickupRule,
    userPortalTexts,
    canSkip,
  } = useReturnModeForm();
  const selected = form.watch("returnMode");
  const t = useCustomText();

  // The hook navigates to the address step; render nothing in the meantime.
  if (canSkip) return null;

  const pickupDateError = form.formState.errors.pickupDate?.message;
  const storeError = form.formState.errors.storeId?.message;

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("returnMode.title")}</HeaderTitle>
        <HeaderDescription>{t("returnMode.description")}</HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      {pickupDateError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>{pickupDateError}</AlertDescription>
        </Alert>
      )}
      {storeError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>
            {t("returnMode.store.noStoreSelectedError.title")}
          </AlertTitle>
          <AlertDescription>
            {t("returnMode.store.noStoreSelectedError.description")}
          </AlertDescription>
        </Alert>
      )}

      <OptionsList>
        <Controller
          control={form.control}
          name="returnMode"
          render={({ field }) => (
            <RadioGroup onValueChange={field.onChange} value={field.value}>
              {availableReturnModes.includes("carrier") && (
                <CarrierOption
                  control={form.control}
                  selected={selected === "carrier"}
                  isPickup={isPickup}
                  pickupRule={pickupRule}
                  error={form.formState.errors.pickupDate?.message}
                />
              )}
              {availableReturnModes.includes("store") && (
                <StoreOption
                  control={form.control}
                  selected={selected === "store"}
                  selectableStores={selectableStores}
                  isRefused={storeReturnState.isRefused}
                  isPending={storeReturnState.isPending && selected !== "store"}
                />
              )}
              {availableReturnModes.includes("retractation") && (
                <RetractationOption />
              )}
            </RadioGroup>
          )}
        />
      </OptionsList>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ exit: true }}
          layout="position"
        >
          <Link to="../summary">
            <Button variant="secondary">
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </Link>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer
          layoutId="line-item-confirm-button"
          variants={{}}
        >
          <Button onClick={onSubmit} disabled={isSubmitBlocked}>
            <EnterIcon />
            {t("common.confirm")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
